// Onglet « Utilisateurs » d'une fiche client — les comptes du CLIENT (realm CLIENTS de l'IdP), leurs
// accès, et l'invitation d'un nouvel utilisateur.
//
// POURQUOI ICI, et pas dans l'écran Utilisateurs : celui-là porte le staff Orgazone (`docs/BRIEF-SuperAdmin.md` :
// « le staff Orgazone uniquement […] pas de comptes clients ici »). Un compte client n'a de sens que
// rattaché à son client — c'est ce rattachement qui décide ce qu'on peut lui accorder (ses licences,
// ses magasins). Le client n'est donc pas un champ du formulaire, c'est le contexte de l'écran.
//
// ⚠️ §23.0 : le statut d'un compte (actif / invité / 2FA / dernière connexion) vient de l'IdP à chaque
// lecture. Quand l'IdP est injoignable, on le DIT — le rattachement reste affiché, le statut non.
//
// ⚠️ CLASSES : `.dcard` / `.modalwrap` / `.modal` / `.modalfoot` / `.opt` / `.note` / `.err` / `.mono`
// — les classes RÉELLES de la feuille de style, celles qu'emploient les onglets voisins.
import { useCallback, useEffect, useId, useState } from 'react';
import { alert, confirm, EmptyState, showToast, useLatest, useOverlayPanel } from '@orgazone/ui-web';
import { dateTimeShort } from '@orgazone/date-format';
import { api, type TenantAccessOptions, type TenantAccount, type TenantGrant } from '../api';
import { formPanelRef, Pill as UiPill } from '../ui';
import { useAuth, can } from '../auth';
import { AccountWizard, BO_ROLES, type BoRole } from './account-wizard';

const Pill = ({ k, children }: { k: string; children: React.ReactNode }) => <UiPill tone={k} shape="tight">{children}</UiPill>;

/** État d'authentification du compte, tel que l'IdP le rapporte — jamais déduit. */
function statutIdp(a: TenantAccount): { k: string; label: string } {
  if (a.idpError) return { k: 'wa', label: 'statut inconnu' };
  if (!a.identity?.exists) return { k: 'da', label: 'identité absente' };
  const s = a.identity.status;
  return s === 'active' ? { k: 'ok', label: 'actif' }
    : s === 'invited' ? { k: 'wa', label: 'invité' }
    : s === 'disabled' ? { k: 'ne', label: 'désactivé' }
    : { k: 'ne', label: s ?? '—' };
}

/** Lecture humaine d'un accès `bo:responsable:376…` — un scope brut ne se lit pas d'un coup d'œil. */
function libelleAcces(scope: string, opts: TenantAccessOptions | null): string {
  const [tool, role, gln] = scope.split(':');
  const outil = opts?.tools.find((t) => t.key === tool)?.label ?? tool;
  const nomRole = opts?.roles.find((r) => r.code === role && r.app === tool)?.name ?? role;
  const magasin = gln ? (opts?.stores.find((s) => s.gln === gln)?.name ?? gln) : null;
  return magasin ? `${outil} · ${nomRole} · ${magasin}` : `${outil} · ${nomRole}`;
}

export { inviteDefaultEmail } from './account-wizard';

export function ClientAccountsTab({ tenantId, tenantName, contactEmail }: { tenantId: string; tenantName: string; contactEmail?: string | null }) {
  const { user } = useAuth();
  // Verbe réservé : gérer les comptes d'un client n'est pas le même droit que gérer le client.
  const canWrite = can(user, 'console.account.write');
  const [accounts, setAccounts] = useState<TenantAccount[] | null>(null);
  const [opts, setOpts] = useState<TenantAccessOptions | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [opening, setOpening] = useState<TenantAccount | null>(null);

  const latest = useLatest();
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : passer d'un client à l'autre pendant la lecture, ou détacher un compte
  //    pendant qu'elle court, ferait afficher la liste des comptes d'un AUTRE client — ou celle d'avant le geste.
  const load = useCallback(async () => {
    const isLatest = latest();
    setErr(null);
    try {
      const [a, o] = await Promise.all([api.tenantAccounts(tenantId), api.tenantAccessOptions(tenantId)]);
      if (!isLatest()) return;
      setAccounts(a.accounts);
      setOpts(o);
    } catch (e) {
      if (!isLatest()) return;
      setErr((e as Error).message); setAccounts(null);
    }
  }, [tenantId, latest]);
  useEffect(() => { void load(); }, [load]);

  const detacher = async (a: TenantAccount) => {
    const ok = await confirm({
      title: `Détacher ${a.email} ?`,
      message: `Ses ${a.grants.length} accès seront révoqués. Le compte lui-même reste dans l’annuaire d’identités — il n’est pas supprimé, seulement détaché de « ${tenantName} ».`,
      confirmLabel: 'Détacher',
    });
    if (!ok) return;
    try {
      const r = await api.detachTenantAccount(tenantId, a.email);
      showToast(`${a.email} détaché — ${r.revoked} accès révoqué(s).`);
      void load();
    } catch (e) { void alert({ title: 'Détachement impossible', message: (e as Error).message }); }
  };

  const revoquer = async (a: TenantAccount, g: TenantGrant) => {
    const ok = await confirm({
      title: 'Retirer cet accès ?',
      message: `${libelleAcces(g.scope, opts)} — l’accès cesse à la prochaine ouverture de session.`,
      confirmLabel: 'Retirer',
    });
    if (!ok) return;
    try { await api.revokeTenantAccess(tenantId, a.email, g.id); showToast('Accès retiré.'); void load(); }
    catch (e) { void alert({ title: 'Retrait impossible', message: (e as Error).message }); }
  };

  if (err) return <div className="err">{err}</div>;
  if (!accounts) return <div className="muted">Chargement des comptes…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>
          comptes du client
        </span>
        <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>{accounts.length} compte{accounts.length > 1 ? 's' : ''}</span>
        <div style={{ flex: 1 }} />
        {canWrite && <button className="btn btn-cta sm" style={{ height: 36 }} onClick={() => setInviting(true)}>Inviter un utilisateur</button>}
      </div>

      {accounts.length === 0 ? (
        <div className="dcard">
          <EmptyState
            icon="users" title="Aucun compte"
            note="Les utilisateurs de ce client se connectent en SSO. Invitez-les ici : l’identité est créée dans l’annuaire Orgazone et leur compte ouvert dans le back-office des magasins choisis."
            action={canWrite ? <button className="ghost btn-ghost" style={{ width: 'auto', padding: '5px 12px' }} onClick={() => setInviting(true)}>+ Inviter un utilisateur</button> : undefined}
          />
        </div>
      ) : accounts.map((a) => {
        const st = statutIdp(a);
        return (
          <div key={a.email} className="dcard" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <b style={{ fontSize: 'var(--t-title)' }}>{a.name || a.email}</b>
              <span className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>{a.email}</span>
              <Pill k={st.k}>{st.label}</Pill>
              {a.identity?.twofa === false && <Pill k="wa">sans 2FA</Pill>}
              <div style={{ flex: 1 }} />
              {canWrite && <button className="ghost btn-ghost" style={{ width: 'auto', padding: '4px 10px' }} onClick={() => setOpening(a)}>Ouvrir le back-office</button>}
              {canWrite && <button className="btn danger soft sm" onClick={() => void detacher(a)}>Détacher</button>}
            </div>

            {/* ⚠️ L'échec de lecture de l'IdP se DIT avec son message réel : sans cela, un compte
                apparaîtrait « sans accès » alors qu'on n'a simplement pas pu les lire. */}
            {a.idpError && <div className="err" style={{ margin: 0 }}>Annuaire d’identités injoignable : {a.idpError} — statut et accès non lus.</div>}

            {!a.idpError && (a.grants.length === 0
              ? <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>Les accès au back-office se lisent dans le back-office lui-même (« Opérateurs &amp; accès ») — la Console ne les relit pas encore.</div>
              : (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                  {/* ⚠️ Des droits inscrits à l'annuaire qu'AUCUN outil ne lit : les montrer comme des accès mentirait. */}
                  <span data-directory-grants style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Inscrits à l’annuaire, appliqués par aucun outil :</span>
                  {a.grants.map((g) => (
                    <span key={g.id} style={{ height: 28, padding: '0 11px', borderRadius: 'var(--r-xs)', display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 'var(--t-dense)', fontWeight: 600, border: '1px solid var(--ink200)' }}>
                      {libelleAcces(g.scope, opts)}
                      {g.expiresAt && <span className="mono" style={{ fontSize: 'var(--t-micro)', opacity: 0.75 }}>jusqu’au {dateTimeShort(g.expiresAt)}</span>}
                      {canWrite && (
                        <button onClick={() => void revoquer(a, g)} title="Retirer cet accès"
                          className="btn-icon danger" style={{ marginLeft: 2 }}>✕</button>
                      )}
                    </span>
                  ))}
                </div>
              ))}

            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
              Invité le {dateTimeShort(a.invitedAt)}{a.invitedBy ? ` par ${a.invitedBy}` : ''}
              {a.identity?.lastLoginAt ? ` · dernière connexion ${dateTimeShort(a.identity.lastLoginAt)}` : ' · jamais connecté'}
            </div>
          </div>
        );
      })}

      {inviting && <AccountWizard preset={{ tenantId, tenantName, contactEmail }} onClose={() => setInviting(false)} onDone={(n) => { setInviting(false); showToast(n); void load(); }} />}
      {opening && opts && <OpenBackOffice tenantId={tenantId} account={opening} opts={opts} onClose={() => setOpening(null)} onDone={() => { setOpening(null); void load(); }} />}
    </div>
  );
}

// ── Modale : ouvrir le back-office des magasins à un compte du client ────────────────────────────
// ⚠️ C'est LE geste qui donne un accès réel : le back-office refuse toute connexion sans son propre compte. Le
//    rôle est l'un des deux qu'il applique ; les « Rôles et droits » de la Console ne s'y appliquent pas encore.
function OpenBackOffice({ tenantId, account, opts, onClose, onDone }: { tenantId: string; account: TenantAccount; opts: TenantAccessOptions; onClose: () => void; onDone: () => void }) {
  const [glns, setGlns] = useState<string[]>(opts.stores.map((s) => s.gln));
  const [role, setRole] = useState<BoRole>('operator');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await api.openBoAccess(tenantId, account.email, { role, glns });
      const label = BO_ROLES.find((x) => x.v === role)!.label;
      showToast(r.created ? `Back-office ouvert à ${account.email} (${label}).`
        : r.addedGlns.length ? `Back-office de ${account.email} étendu à ${r.addedGlns.length} magasin${r.addedGlns.length > 1 ? 's' : ''}.`
          : `${account.email} avait déjà le back-office de ces magasins.`);
      onDone();
    } catch (e2) { setErr((e2 as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <form className="modal" ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 id={titleId}>Ouvrir le back-office à {account.name || account.email}</h3>
        {/* ⚠️ AUCUNE CONDITION DE LICENCE : elle est commerciale, jamais un verrou d'usage (route `…/bo-access`). */}
        {opts.stores.length === 0 ? (
          <>
            <div className="note">Ce client n’a encore aucun magasin : il n’y a rien à ouvrir.</div>
            <div className="modalfoot"><button type="button" className="ghost btn-ghost" onClick={onClose}>Fermer</button></div>
          </>
        ) : (
          <>
            <div id="ob-stores" className="opt" style={{ marginTop: 4 }}>Magasins</div>
            <div role="group" aria-labelledby="ob-stores" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              {opts.stores.map((s) => (
                <label key={s.gln} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontWeight: 500 }}>
                  <input type="checkbox" checked={glns.includes(s.gln)} style={{ width: 'auto' }}
                    onChange={(e) => setGlns(e.target.checked ? [...glns, s.gln] : glns.filter((g) => g !== s.gln))} />
                  {s.name} <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{s.gln}</span>
                </label>
              ))}
            </div>
            <div id="ob-role" className="opt" style={{ marginTop: 12 }}>Rôle dans le back-office</div>
            <div role="radiogroup" aria-labelledby="ob-role" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              {BO_ROLES.map((r) => (
                <label key={r.v} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: 0, fontWeight: 500 }}>
                  <input type="radio" name="bo-role" checked={role === r.v} onChange={() => setRole(r.v)} style={{ width: 'auto', marginTop: 3 }} />
                  <span><b>{r.label}</b> — <span style={{ color: 'var(--ink500)' }}>{r.desc}</span></span>
                </label>
              ))}
            </div>
            {err && <div className="err">{err}</div>}
            <div className="modalfoot">
              <button type="button" className="ghost btn-ghost" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-cta" disabled={busy || glns.length === 0}>{busy ? 'Ouverture…' : 'Ouvrir le back-office'}</button>
            </div>
            <p className="note">Un compte garde un seul rôle dans le back-office : pour le changer, passez par l’écran « Opérateurs &amp; accès » du back-office.</p>
          </>
        )}
      </form>
    </div>
  );
}
